import React, { useEffect, useState } from "react";
import { getEstimaticsCurrentProjectStore, getUserStore } from "../../../data/DataStores";
import {
	IEstimateDataForUI,
	IEstimaticsData,
	IMissingMappings,
	IQueuedItemToProcess,
	ITupleOutputForUI,
	getQueuedItemToProcessStatusName,
} from "../../../shared/common/EstimateEngine/EstimateEngineTypes";
import ReactJson from "react-json-view";
import EstimateRoomHeader from "./EstimateTable/EstimateRoomHeader";
import EstimateTableHeader from "./EstimateTable/EstimateTableHeader";
import EstimateTableRow from "./EstimateTable/EstimateTableRow";
import EstimateTableFooter from "./EstimateTable/EstimateTableFooter";
import EngineUtils from "../../../shared/common/EngineUtils";

import { getProcessedEstimatesStore, getProcessedEstimatesStoreDebug } from "../../../data/ProcessedEstimatesStore";
import { appFeatures } from "../../../shared/common/AppFeatures";
import { observer } from "mobx-react";
import SharedUtils from "../../../shared/common/SharedUtils";
import { UploadingAnimation } from "../../pages/EstimaticsProjectPage";
import LottieAnimation from "../../../components/ui/utils/LottieAnimation";
import TopMostPortal from "../../../components/ui/utils/Portals";
import { ErrorResolutionPanel, SelectOption } from "./ErrorResolutionPanel";
import { logDebug, logError } from "../../../shared/common/logger";
import RestAPI from "../../../utils/restAPI";
import { ImagesDatabase } from "../../../components/ui/utils/ImageCropping";
import { toastNotify } from "../../../shared/client/FrontendUtils";
import { LassoSimpleButtonFull } from "../../../components/ui/utils/LassoButtons";
import { IXactimateToolRowData } from "../../../shared/common/Azure";
import Select from "react-select";
import { makeAutoObservable, makeObservable } from "mobx";

// interface IExtractedImage {
// 	cellsRawDataPath: string,
// 	cellsWithInputPath: string,
// 	classification: string,
// 	estimatePath: string,
// 	extractedFrom: string,
// 	layoutStoragePath: string,
// 	matchingRowsPath: string,
// 	mime: string,
// 	originalName: string,
// 	readStoragePath: string,
// 	storagePath: string
// }

// interface IEstimateEngineEstimate {
// 	endProcessTime: number,
// 	estimateId: string,
// 	extractedImages: IImageMetadata[],
// 	itemId: string,
// 	organizationId: string,
// 	processorId: string,
// 	queueEntryTime: number,
// 	startProcessTime: number,
// 	status: string;
// 	originalUploadedFiles: {
// 		mime: string,
// 		originalFileName: string,
// 		storagePath: string
// 	}[]
// }

const EstimataLineItem: React.FC<{ lineItem: any; tuple: any }> = ({ lineItem, tuple }) => {
	const [showJson, setShowJson] = React.useState(false);
	return (
		<div className="text-red-400">
			{lineItem.outLineItems.map((outLineItem: any, index: number) => {
				return (
					<div key={index} className="bg-green-100 btn" onClick={() => setShowJson(!showJson)}>
						{outLineItem.join(" ")}
						<div
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
							}}
						>
							{showJson && (
								<ReactJson
									src={tuple}
									collapsed={true}
									collapseStringsAfterLength={64}
									displayDataTypes={false}
									displayObjectSize={false}
								/>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
const EstimateDebugView: React.FC<{}> = ({}) => {
	if (appFeatures.shouldShowEngineDebugInfo(getUserStore().user!) === false) return null;
	const [mds, setMds] = useState<any[]>([]);
	const item =
		getProcessedEstimatesStore().getQueueItemsForEstimateId(getEstimaticsCurrentProjectStore().estimaticsData.estimateId)?.[0] || null;
	logDebug("estimate", item);
	useEffect(() => {
		async function fetch() {
			setMds([]);
			for (let i = 0; i < (item?.extractedImages?.length || 0); i++) {
				const metadata = await RestAPI.fetchAPI("/rest/estimates/get_estimate", "GET", {
					estimatePath: item!.extractedImages![i].estimatePath,
					requestType: "devMetadata",
				});
				setMds((prev) => [...prev, metadata]);
				// logDebug(i, "metadata", metadata);
			}
		}
		fetch();
	}, []);
	// logDebug('estimate', estimate)
	return (
		<div>
			<div>Estimate View</div>
			<div className="text-blue-300">{item?.itemId}</div>
			{mds?.map((page, index) => {
				logDebug("page", page);
				return (
					<div key={index}>
						<span className="bg-sky-300">page : {index}</span>
						{page?.map((tuple: any, index: number) => {
							return (
								<div key={index}>
									{tuple.tupleId}:{" "}
									{tuple.lineItemsMetadata.map((lineItem: any, index: number) => {
										return <EstimataLineItem lineItem={lineItem} tuple={tuple} key={index} />;
									})}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};

const EstimateEnginePanel: React.FC<{ estimateId: string }> = observer(({ estimateId }) => {
	const userStore = getUserStore();

	const processedEstimatesStore = getProcessedEstimatesStore();
	const processedEstimatesStoreDebug = getProcessedEstimatesStoreDebug();
	// const [myEstimates, setMyEstimates] = useState<IEstimateData[]>([]);
	const [myQueueItems, setMyQueueItems] = useState<IQueuedItemToProcess[]>([]);
	const [parentQueueItem, setParentQueueItem] = useState<IQueuedItemToProcess | null>(null);
	// const [allPages, setAllPages] = useState<{ page: IPageForUI, documentIndex: number, pageIndex: number, document: IDocumentForUI }[]>([]);
	const currentProjectStore = getEstimaticsCurrentProjectStore();
	const [curPage, setCurrentPage] = useState(0);
	const [collapseErrors, setCollapseErrors] = useState(true);
	const [collapseComplements, setCollapseComplements] = useState(true);
	const [collapseFreeFormFlags, setCollapseFreeFormFlags] = useState(true);
	const cachedImages = React.useRef<any>({});
	const mergingRoomRef = React.useRef(false);
	useEffect(() => {
		// pre-cache the estimate's images
		processedEstimatesStore.allCurrentViewPages.map((page) => {
			if (!page.page.fullPageImagePath) return;
			const url = RestAPI.getStorageFileUrl(page.page.fullPageImagePath);
			if (cachedImages.current[url]) return;
			cachedImages.current[url] = true;
			ImagesDatabase.getInstance().getImage(url);
		});
	}, [processedEstimatesStore.allCurrentViewPages]);
	useEffect(() => {
		processedEstimatesStore.setCurrentViewEstimateId(estimateId);
		// const myEstimates = processedEstimatesStore.estimates.filter((estimate) => estimate.estimateQueueItem.estimateId === estimateId);
		// setMyEstimates(myEstimates);
		// const _documents = myEstimates.map((estimate) => estimate.processedForUI);
		// const allPages: {
		// 	page: IPageForUI,
		// 	documentIndex: number,
		// 	pageIndex: number,
		// 	document: IDocumentForUI
		// }[] = [];
		// _documents.forEach((document, documentIndex) => {
		// 	document.pages.forEach((page, pageIndex) => {
		// 		allPages.push({
		// 			page,
		// 			documentIndex,
		// 			pageIndex,
		// 			document
		// 		});
		// 	});
		// });
		// setAllPages(allPages);
		// logDebug('allPages', allPages);
		// logDebug('myEstimates', myEstimates);
		// logDebug('_documents', _documents);
	}, [processedEstimatesStore.estimates]);

	useEffect(() => {
		const data = processedEstimatesStore.getQueueItemsForEstimateId(estimateId);
		const parentItem = processedEstimatesStore.getParentQueueItemForEstimateId(estimateId);
		if (data.length && parentItem) {
			setMyQueueItems(data);
			setParentQueueItem(parentItem);
			if (data.every((item) => item.status >= 100) && parentItem.autoMergeCompleted) {
				// TODO: AND PARENT POSTPROCESSING = COMPLETE
				processedEstimatesStore.setCurrentViewEstimateId(estimateId);
			}
		}
	}, [processedEstimatesStore.getQueueItemsForEstimateId(estimateId)]);

	useEffect(() => {
		async function tryMergeRooms() {
			// await currentProjectStore.setValue("autoMergeRoomCompleted", false, true);
			logDebug("autoMergeRoomCompleted=", currentProjectStore.getValue<boolean>("autoMergeRoomCompleted"));
			if (!currentProjectStore.getValue<boolean>("autoMergeRoomCompleted")) {
				logDebug("try to merge rooms", processedEstimatesStore.allCurrentViewPages);
				// find rooms with the same names and merge them
				try {
					const roomsBuckets = processedEstimatesStore.allCurrentViewPages.reduce((acc, page, index) => {
						if (page.page.mergeWith) return acc;
						const pageInfoData = EngineUtils.pageDataFromPageInfo(page);
						const roomName = pageInfoData.roomName.toLowerCase();
						if (!acc[roomName]) {
							acc[roomName] = [];
						}
						acc[roomName].push(index);
						return acc;
					}, {} as { [key: string]: number[] });

					const dups = Object.values(roomsBuckets).filter((bucket) => bucket.length > 1);
					if (dups.length) {
						for (let i = 0; i < dups.length; i++) {
							const dup = dups[i];
							try {
								const room0 = processedEstimatesStore.allCurrentViewPages[dup[0]];
								const room1 = processedEstimatesStore.allCurrentViewPages[dup[1]];
								const pageId0 = EngineUtils.pageDataFromPageInfo(room0).pageId;
								const pageId1 = EngineUtils.pageDataFromPageInfo(room1).pageId;
								await RestAPI.fetchAPI("/rest/estimates/mergePages", "POST", {
									estimateId: currentProjectStore.currentEstimateID,
									sourcePageId: pageId0,
									destinationPageId: pageId1,
								});
								processedEstimatesStore.locallyMergePages(pageId0, pageId1);
								logDebug("Rooms merged successfully", pageId0, pageId1);
							} catch (err) {
								logError("Error merging rooms", err);
							}
						}
						logDebug(
							"all rooms merged successfully",
							Object.entries(roomsBuckets)
								.filter(([_, v]) => v.length > 1)
								.map(([k, v]) => k),
						);
					}
				} catch (err) {
					logError("Error merging rooms", err);
				}
				// TODO :: reload the estimate without refreshing the page
				await currentProjectStore.setValue("autoMergeRoomCompleted", true, true);
			}
		}
		if (processedEstimatesStore.allCurrentViewPages.length > 0) {
			if (mergingRoomRef.current) {
				//tryMergeRooms can trigger the same effect when updating the the allCurrentViewPages
				return;
			}
			mergingRoomRef.current = true;
			tryMergeRooms();
			mergingRoomRef.current = false;
		}
	}, [processedEstimatesStore.allCurrentViewPages]);

	useEffect(() => {
		if (curPage === currentProjectStore.selectedRoomTabIndex) return;
		const d = document.getElementById(`scrollPage-${currentProjectStore.selectedRoomTabIndex}`);
		if (d) {
			d.scrollIntoView({ behavior: "smooth", block: "start" });
			setCurrentPage(currentProjectStore.selectedRoomTabIndex);
		}
	}, [currentProjectStore.selectedRoomTabIndex]);

	function getTupleOutputById(tupleId: string, pageId: string): ITupleOutputForUI | undefined {
		// debugger;
		// find the page
		const pageInfo = processedEstimatesStore.allCurrentViewPages.find((pi) => {
			const pageInfoData = EngineUtils.pageDataFromPageInfo(pi);
			return pageInfoData.pageId === pageId;
		});
		if (!pageInfo) return undefined;

		// check if it merged with another page and add the tuples from that page as well
		const mergedWith = processedEstimatesStore.allCurrentViewPages
			.filter((pi) => {
				return pi.page.mergeWith === pageId;
			})
			.map((pi) => {
				const pageInfoData = EngineUtils.pageDataFromPageInfo(pi);
				return { ...pi, pageInfoData };
			});

		const allTuples = [...pageInfo.page.tuplesOutput, ...mergedWith.flatMap((_pageInfo) => _pageInfo.page.tuplesOutput)].filter(
			(tuple) => !tuple.deleted,
		);

		return allTuples.find((tuple) => tuple.tupleId === tupleId);
	}

	function validateAllRequiredFieldsAreFilledBeforSendingToXactimate(estimaticsData: IEstimaticsData) {
		let haveAddress = false;
		const errors: string[] = [];
		if (!estimaticsData.propertyAddressComponents || estimaticsData.propertyAddressComponents === "[]") {
			errors.push("Property Address");
			// alert("Please enter property address first");
		} else {
			haveAddress = true;
		}
		if (haveAddress) {
			// have property address, so we need to check zip code
			const addressComponents = JSON.parse(estimaticsData.propertyAddressComponents);
			if (addressComponents.length > 0 && !addressComponents.some((a: any) => a.types.includes("postal_code"))) {
				// alert("Please include zip-code in the Property address.");
				errors.push("Zip Code");
			}
		}
		if (!estimaticsData.dateContacted || !estimaticsData.dateCreated) {
			// alert("Please enter date contacted and date created first");
			errors.push("Date Contacted");
		}
		if (!estimaticsData.dateOfLoss) {
			errors.push("Date of Loss");
		}
		if (errors.length > 0) {
			const errorString = `The following fields are required:\n\n${errors.join("\n")}`;
			alert(errorString);
			return false;
		} else {
			const dateContacted = SharedUtils.from_US_EN_LocaleDateString(estimaticsData.dateContacted).getTime();
			const dateOfLoss = SharedUtils.from_US_EN_LocaleDateString(estimaticsData.dateOfLoss).getTime();
			if (dateContacted < dateOfLoss) {
				alert("Date contacted should be greater or equal to date of loss");
				return false;
			}
		}

		return true;
	}

	// logDebug('estimates', estimates);
	return (
		<div>
			{processedEstimatesStoreDebug.showQAPanel && appFeatures.shouldShowEngineDebugInfo(userStore.user!) && (
				<h1>Estimate Engine Panel : number of estimates {myQueueItems.length}</h1>
			)}
			{processedEstimatesStoreDebug.showQAPanel &&
				appFeatures.shouldShowEngineDebugInfo(userStore.user!) &&
				myQueueItems.map((estimate, index) => {
					return (
						<div key={index}>
							{estimate.originalUploadedFiles[0].originalFileName}:
							<span className="text-red-500">{getQueuedItemToProcessStatusName(estimate.status)}</span>
						</div>
					);
				})}
			<div className="h-[52px] p-2 bg-[#f2f3f6] rounded-lg border border-[#f2f3f6] justify-between items-center inline-flex w-[100%] mt-8">
				<div className="max-w-[85%] overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
					<div className="justify-start items-center gap-2 flex min-w-fit">
						{processedEstimatesStore.allCurrentViewPages.map((page, pageIndex) => {
							if (page.page.mergeWith) {
								return null;
							}
							const roomName = SharedUtils.capitalizeFirstLetterOfEachWord(
								!page.document.roomName || page.document.roomName.toLowerCase() === "n/a"
									? "Unnamed Room"
									: page.document.roomName,
							);
							return (
								<div
									onClick={() => {
										currentProjectStore.selectedRoomTabIndex = pageIndex;
									}}
									id={`roomIndex-${pageIndex}`}
									key={pageIndex}
									className={`px-3 py-2 rounded-md justify-center items-center gap-2 flex btn ${
										pageIndex === curPage ? "bg-white  shadow" : ""
									}`}
								>
									<div className="text-[#344053] text-sm font-semibold font-['Inter'] leading-tight">{roomName}</div>
								</div>
							);
						})}
					</div>
				</div>
				<div className="px-3 py-2 rounded-md justify-center items-center gap-2 flex">
					<div className="w-5 h-5 relative" />
					<LassoSimpleButtonFull
						customButtonStyle={{ height: "40px" }}
						caption="Send to Xactimate"
						image="/assets/ui/send.png"
						onClick={async () => {
							if (!validateAllRequiredFieldsAreFilledBeforSendingToXactimate(currentProjectStore.estimaticsData)) {
								return;
							}
							await _veriskLocalStore.publishEstimate(estimateId);
							// currentProjectStore.showLoadingAnimation = true;
							// let transactionId = "";
							// try {
							// 	const missingMappings = await RestAPI.fetchAPI("/rest/estimates/exportToXactimate", "GET", {
							// 		estimateId: estimateId,
							// 		dryRun: true,
							// 	});
							// 	if (missingMappings && missingMappings.length) {
							// 		_veriskLocalStore.missingMappings = missingMappings.splice(0, 2);
							// 		currentProjectStore.showLoadingAnimation = false;
							// 		return;
							// 	}

							// 	transactionId = await RestAPI.fetchAPI("/rest/estimates/exportToXactimate", "GET", {
							// 		estimateId: estimateId,
							// 	});
							// 	if (transactionId) {
							// 		toastNotify("Estimate created! Uploading images... ", false);
							// 		currentProjectStore.showLoadingAnimation = false;
							// 		transactionId = await RestAPI.fetchAPI("/rest/estimates/exportToXactimate", "GET", {
							// 			estimateId: estimateId,
							// 			transactionId,
							// 			photosOnly: true,
							// 		});
							// 		if (transactionId) {
							// 			toastNotify("Images uploaded successfully", false);
							// 		} else {
							// 			currentProjectStore.setValue("uploadImagesFailed", true);
							// 			toastNotify("Error uploading images", true);
							// 		}
							// 	} else {
							// 		toastNotify("Error exporting to Xactimate", true);
							// 	}
							// } catch (e) {
							// 	if (!transactionId) {
							// 		toastNotify("Error exporting to Xactimate", true);
							// 	} else {
							// 		currentProjectStore.setValue("uploadImagesFailed", true);
							// 		toastNotify("Error uploading images", true);
							// 	}
							// }
							// currentProjectStore.showLoadingAnimation = false;
						}}
					/>
				</div>
			</div>
			<div className="w-full flex items-center justify-center">
				{(myQueueItems.some((item) => item.status < 100) || !parentQueueItem?.autoMergeCompleted) && (
					<div className="text-xl mt-4">
						Processing...
						<LottieAnimation animationData={UploadingAnimation} w={25} h={25} />
					</div>
				)}
			</div>

			{!!currentProjectStore.estimaticsData.autoMergeRoomCompleted &&
				processedEstimatesStore.allCurrentViewPages.map((pageInfo, index) => {
					if (pageInfo.page.mergeWith) {
						return null;
					}
					const pageInfoData = EngineUtils.pageDataFromPageInfo(pageInfo);
					// if (pageInfoData.item.extractedImages?.[0]?.dataForUIPath === "creates_by_images") {
					// 	pageInfoData.pageId = pageInfoData.item.extractedImages?.[0]?.pageId!;
					// }
					const mergedWith = processedEstimatesStore.allCurrentViewPages
						.filter((pi) => {
							return pi.page.mergeWith === pageInfoData.pageId;
						})
						.map((pi) => {
							const pageInfoData = EngineUtils.pageDataFromPageInfo(pi);
							return { ...pi, pageInfoData };
						});

					// TODO :: this code is a mess. Clean it up
					const errorTuples = [
						...pageInfo.page.tuplesOutput
							.map((tuple) => ({ tuple, pageInfoData }))
							.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						...mergedWith.flatMap((_pageInfo) =>
							_pageInfo.page.tuplesOutput
								.map((tuple) => ({
									tuple,
									pageInfoData: _pageInfo.pageInfoData,
								}))
								.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						),
					].filter((tuple) => !tuple.tuple.deleted && tuple.tuple.hasError);

					const freeFormFlagTuples = [
						...pageInfo.page.tuplesOutput
							.map((tuple) => ({ tuple, pageInfoData }))
							.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						...mergedWith.flatMap((_pageInfo) =>
							_pageInfo.page.tuplesOutput
								.map((tuple) => ({
									tuple,
									pageInfoData: _pageInfo.pageInfoData,
								}))
								.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						),
					].filter((tuple) => !tuple.tuple.deleted && tuple.tuple.isFreeFormFlag);

					const notErrorTuples = [
						...pageInfo.page.tuplesOutput
							.map((tuple) => ({ tuple, pageInfoData }))
							.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						...mergedWith.flatMap((_pageInfo) =>
							_pageInfo.page.tuplesOutput
								.map((tuple) => ({
									tuple,
									pageInfoData: _pageInfo.pageInfoData,
								}))
								.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						),
					]
						.filter(
							(tuple) =>
								!tuple.tuple.deleted && !tuple.tuple.hasError && !tuple.tuple.isComplementOf && !tuple.tuple.isFreeFormFlag,
						)
						.sort((a, b) => {
							const aOrder = a.tuple.lineItems[0].orderIndex;
							const bOrder = b.tuple.lineItems[0].orderIndex;
							if (aOrder === undefined || bOrder === undefined) {
								return 0;
							}
							return aOrder - bOrder;
						});

					const notErrorTuplesLikelyComplements = [
						...pageInfo.page.tuplesOutput
							.map((tuple) => ({ tuple, pageInfoData }))
							.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						...mergedWith.flatMap((_pageInfo) =>
							_pageInfo.page.tuplesOutput
								.map((tuple) => ({
									tuple,
									pageInfoData: _pageInfo.pageInfoData,
								}))
								.filter((tuple) => tuple.tuple.lineItems.some((li) => !li.deleted)),
						),
					]
						.filter((tuple) => {
							if (tuple.tuple.deleted || tuple.tuple.hasError) {
								return false;
							}
							if (!!tuple.tuple.isComplementOf) {
								const parent = getTupleOutputById(tuple.tuple.isComplementOf, tuple.pageInfoData.pageId);
								if (!parent) {
									return false;
								}
								if (parent.hasError) {
									return false;
								}
								return true;
							}
							return false;
						})
						.sort((a, b) => a.tuple.isComplementOf!.localeCompare(b.tuple.isComplementOf!));

					let notErrorTuplesLikelyComplementsMerged: typeof notErrorTuplesLikelyComplements = [];
					if (notErrorTuplesLikelyComplements.length > 0) {
						const clonedNotErrorTuplesLikelyComplements = SharedUtils.clone(
							notErrorTuplesLikelyComplements,
						) as typeof notErrorTuplesLikelyComplements;
						clonedNotErrorTuplesLikelyComplements.forEach((tuple) => {
							tuple.tuple.lineItems.forEach((lineItem) => {
								// Object.defineProperty(lineItem, "$$complementTupleId", {
								// 	value: tuple.tuple.tupleId,
								// 	enumerable: false, // Makes the property non-enumerable
								// });
								lineItem.$$complementTupleId = tuple.tuple.tupleId; // store the original tupleId in the lineItem
							});
						});
						notErrorTuplesLikelyComplementsMerged.push({ ...clonedNotErrorTuplesLikelyComplements[0] });
						for (let i = 1; i < clonedNotErrorTuplesLikelyComplements.length; i++) {
							if (
								clonedNotErrorTuplesLikelyComplements[i].tuple.isComplementOf !==
								notErrorTuplesLikelyComplementsMerged[notErrorTuplesLikelyComplementsMerged.length - 1].tuple.isComplementOf
							) {
								notErrorTuplesLikelyComplementsMerged.push(clonedNotErrorTuplesLikelyComplements[i]);
							} else {
								notErrorTuplesLikelyComplementsMerged[
									notErrorTuplesLikelyComplementsMerged.length - 1
								].tuple.lineItems.push(...clonedNotErrorTuplesLikelyComplements[i].tuple.lineItems);
							}
						}
					}

					// logDebug("notErrorTuplesLikelyComplements", notErrorTuplesLikelyComplementsMerged);
					// logDebug("notErrorTuples", notErrorTuples);
					return (
						<div id={`scrollPage-${index}`} key={index}>
							{processedEstimatesStoreDebug.showQAPanel && appFeatures.shouldShowEngineDebugInfo(userStore.user!) && (
								<div>itemId: {pageInfo.document.itemForDebugOnly?.itemId}</div>
							)}
							<div key={pageInfo.documentIndex} className="w-[100%]  flex justify-between mt-8">
								<div className="w-16"></div>
								<div className="w-[100%]">
									{processedEstimatesStoreDebug.showQAPanel && appFeatures.shouldShowEngineDebugInfo(userStore.user!) && (
										<div>
											ROOM:<span className="text-red-500">$TBD ({pageInfo.documentIndex})</span>
										</div>
									)}
									<div className="w-[100%]  bg-white rounded-xl shadow border border-[#eaecf0] flex-col justify-start items-start inline-flex">
										<EstimateRoomHeader
											itemForDebugOnly={
												pageInfoData.itemForDebugOnly ||
												(processedEstimatesStore.getQueueItemsForEstimateId(estimateId)?.[0] ?? null)
											}
											pageId={pageInfoData.pageId}
											roomName={pageInfoData.roomName}
										/>
										<EstimateTableHeader />

										<div className="w-full">
											{errorTuples.length > 0 && (
												<div
													className="btn h-[72px] w-full flex-col justify-start items-start inline-flex"
													onClick={() => setCollapseErrors(!collapseErrors)}
												>
													<div className="w-full h-[72px] px-6 bg-[#fffaeb] hover:bg-[#FEF0C7] border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex">
														<div className="self-stretch justify-start items-center flex">
															{/* <img src="/assets/ui/trash-02.svg" className="w-5 h-5 m-4" /> */}
															<div className="w-[366px] self-stretch pl-5 pr-4 justify-start items-center gap-1.5 flex">
																<div className="text-center text-[#db6803] text-base font-black font-['Hanken Grotesk'] leading-snug">
																	{errorTuples.length}
																</div>
																<div className="text-center text-[#db6803] text-base font-normal font-['Hanken Grotesk'] leading-normal">
																	Issues found
																</div>
															</div>
														</div>
														<div className="justify-end items-center gap-4 flex">
															<div className="px-3.5 py-2 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex btn">
																<div className="justify-center text-center items-center gap-2 flex">
																	<div className="text-[#344053] min-w-12 text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
																		{collapseErrors ? "Resolve" : "Hide"}
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											)}
										</div>
										<div className="w-full bg-[#EAECF0]">
											{!collapseErrors && errorTuples.length > 0 && (
												<div className="">
													{errorTuples.map((tuple, tupleIndex) => {
														return (
															<EstimateTableRow
																getTupleOutputById={getTupleOutputById}
																pageId={tuple.pageInfoData.pageId}
																key={tuple.tuple.tupleId}
																tuple={tuple.tuple}
																tupleIndex={tupleIndex}
																fullPageImagePath={tuple.pageInfoData.page.fullPageImagePath}
															/>
														);
													})}
													<div className="w-full flex justify-end items-center px-5 py-3">
														<LassoSimpleButtonFull
															onClick={async (e) => {
																const yesNo = window.confirm(
																	"Are you sure you want to discard all errors?",
																);
																if (!yesNo) return;
																getEstimaticsCurrentProjectStore().showLoadingAnimation = true;
																try {
																	// TODO :: this is very quick and dirty and inefficient. We need to collect all the tuple Ids and call the API once
																	const prms: Promise<boolean>[] = [];
																	errorTuples.map((tuple, tupleIndex) => {
																		tuple.tuple.deleted = true;
																		prms.push(
																			processedEstimatesStore.updateOrCreateLineItem(
																				tuple.tuple.tupleId,
																				getEstimaticsCurrentProjectStore().estimaticsData
																					.estimateId,
																				tuple.pageInfoData.pageId,
																				tuple.tuple,
																			),
																		);
																	});
																	await Promise.all(prms);
																	toastNotify("Errors discarded successfully", false);
																} catch (err) {
																	logError("Error discarding errors", err);
																	toastNotify("Error discarding errors", true);
																}

																getEstimaticsCurrentProjectStore().showLoadingAnimation = false;
															}}
															caption="Discard All"
															customButtonStyle={{ height: 32, backgroundColor: "#bb0000" }}
														></LassoSimpleButtonFull>
													</div>
												</div>
											)}
										</div>
										<div className="w-full">
											{freeFormFlagTuples.length > 0 && (
												<div
													className="btn h-[72px] w-full flex-col justify-start items-start inline-flex "
													onClick={() => setCollapseFreeFormFlags(!collapseFreeFormFlags)}
												>
													<div className="w-full h-[72px] px-6 bg-[#ffe4e6] hover:bg-[#ffc5cb] border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex">
														<div className="self-stretch justify-start items-center flex">
															<div className="w-[366px] self-stretch pl-5 pr-4 justify-start items-center gap-1.5 flex">
																<div className="text-center text-[#B00020] text-base font-['Hanken Grotesk'] leading-snug font-black ">
																	{freeFormFlagTuples.length}
																</div>
																<div className="text-center text-[#B00020] text-base font-['Hanken Grotesk'] font-normal leading-normal">
																	Free Form Cells identified
																</div>
															</div>
														</div>
														<div className="justify-end items-center gap-4 flex">
															<div className="px-3.5 py-2 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex btn">
																<div className="justify-center text-center items-center gap-2 flex">
																	<div className="text-[#344053] min-w-12 text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
																		{collapseFreeFormFlags ? "Show" : "Hide"}
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											)}
										</div>
										<div className="w-full bg-[#EAECF0]">
											{!collapseFreeFormFlags && freeFormFlagTuples.length > 0 && (
												<div className="">
													{freeFormFlagTuples.map((tuple, tupleIndex) => {
														return (
															<EstimateTableRow
																getTupleOutputById={getTupleOutputById}
																pageId={tuple.pageInfoData.pageId}
																key={tuple.tuple.tupleId}
																tuple={tuple.tuple}
																tupleIndex={tupleIndex}
																fullPageImagePath={tuple.pageInfoData.page.fullPageImagePath}
															/>
														);
													})}
												</div>
											)}
										</div>
										<div className="w-full">
											{notErrorTuplesLikelyComplementsMerged.length > 0 && (
												<div
													className="btn h-[72px] w-full flex-col justify-start items-start inline-flex "
													onClick={() => setCollapseComplements(!collapseComplements)}
												>
													<div className="w-full h-[72px] px-6 bg-[#eff8ff] hover:bg-[#e0f8ff] border-l border-r border-b border-[#eaecf0] justify-between items-center inline-flex">
														<div className="self-stretch justify-start items-center flex">
															<div className="w-[366px] self-stretch pl-5 pr-4 justify-start items-center gap-1.5 flex">
																<div className="text-center text-[#194084] text-base font-['Hanken Grotesk'] leading-snug font-black ">
																	{notErrorTuplesLikelyComplements.length}
																</div>
																<div className="text-center text-[#194084] text-base font-['Hanken Grotesk'] font-normal leading-normal">
																	Recommendations
																</div>
															</div>
														</div>
														<div className="justify-end items-center gap-4 flex">
															<div className="px-3.5 py-2 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex btn">
																<div className="justify-center text-center items-center gap-2 flex">
																	<div className="text-[#344053] min-w-12 text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
																		{collapseErrors ? "Show" : "Hide"}
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											)}
										</div>
										<div className="w-full ">
											{!collapseComplements && notErrorTuplesLikelyComplements.length > 0 && (
												<div className="">
													{notErrorTuplesLikelyComplementsMerged.map((tuple, tupleIndex) => {
														// logDebug("tuple", tuple);
														return (
															<EstimateTableRow
																getTupleOutputById={getTupleOutputById}
																pageId={tuple.pageInfoData.pageId}
																key={tuple.tuple.tupleId}
																tuple={tuple.tuple}
																tupleIndex={tupleIndex}
																fullPageImagePath={tuple.pageInfoData.page.fullPageImagePath}
															/>
														);
													})}
												</div>
											)}
										</div>
										<div className="w-full">
											{notErrorTuples.map((tuple, tupleIndex) => {
												return (
													<EstimateTableRow
														getTupleOutputById={getTupleOutputById}
														pageId={tuple.pageInfoData.pageId}
														key={tuple.tuple.tupleId}
														tuple={tuple.tuple}
														tupleIndex={tupleIndex}
														fullPageImagePath={tuple.pageInfoData.page.fullPageImagePath}
													/>
												);
											})}
										</div>
										<EstimateTableFooter pageId={pageInfoData.pageId} roomName={pageInfoData.roomName} />
									</div>
								</div>
								<div className="w-16"></div>
							</div>
						</div>
					);
				})}

			{appFeatures.isDebug(true) && (
				<div className="flex flex-col">{processedEstimatesStoreDebug.showQAPanel && <EstimateDebugView />}</div>
			)}
			{processedEstimatesStore.showErrorResolutionPanelForTuple && (
				<TopMostPortal>
					<ErrorResolutionPanel
						tuple={processedEstimatesStore.showErrorResolutionPanelForTuple.tuple}
						pageId={processedEstimatesStore.showErrorResolutionPanelForTuple.pageId}
					/>
				</TopMostPortal>
			)}
			{_veriskLocalStore.missingMappings.length > 0 && (
				<TopMostPortal>
					<MissingMappingDialog />
				</TopMostPortal>
			)}
		</div>
	);
});
const hardCodedCategories = [
	"DRY",
	"PNT",
	"FNC",
	"FCC",
	"ACT",
	"APP",
	"CAB",
	"CAP CONT",
	"CEL CONT",
	"CLN",
	"CPS CONT",
	"CGN CONT",
	"CHF CONT",
	"CP",
	"CUP CONT",
	"CWH CONT",
	"DMO",
	"DOR",
	"ELE",
	"FCR",
	"FCS",
	"FCT",
	"FCV",
	"FCW",
	"FEE",
	"FNH",
	"FPL",
	"FRM",
	"HVC",
	"INM",
	"INS",
	"LAB",
	"LIT",
	"LND",
	"MBL",
	"MPR",
	"MSD",
	"ORI",
	"PLA",
	"PLM",
	"PNL",
	"PTG",
	"SCF",
	"SDG",
	"STL",
	"STR",
	"TBA",
	"TIL",
	"TMP",
	"WDA",
	"WDP",
	"WDR",
	"WDS",
	"WDT",
	"WDV",
	"WDW",
	"WPR",
	"CON",
	"CNC",
	"CPS",
];

class VeriskLocalStore {
	constructor() {
		makeAutoObservable(this);
		try {
			const addedCategories = window.localStorage.getItem("addedCategories");
			if (addedCategories && JSON.parse(addedCategories).length) {
				this._addedCategories = JSON.parse(addedCategories);
			}
		} catch (err) {}
	}

	private _missingMappings: IMissingMappings[] = [];
	public get missingMappings() {
		return this._missingMappings;
	}
	public set missingMappings(value: IMissingMappings[]) {
		this._missingMappings = value;
	}

	private _fixedMapping: IXactimateToolRowData[] = [];
	public get fixedMapping() {
		return this._fixedMapping;
	}
	public set fixedMapping(value: IXactimateToolRowData[]) {
		this._fixedMapping = value;
	}

	private _addedCategories: string[] = [];

	public get categories() {
		return [...hardCodedCategories, ...this._addedCategories];
	}

	public addCategory(cat: string) {
		window.localStorage.setItem("addedCategories", JSON.stringify([...this._addedCategories, cat]));
		this._addedCategories.push(cat);
	}

	async publishEstimate(estimateId: string) {
		const currentProjectStore = getEstimaticsCurrentProjectStore();
		currentProjectStore.showLoadingAnimation = true;
		let transactionId = "";
		try {
			const encodedMappings = btoa(JSON.stringify(this._fixedMapping));
			const urlSafeEncodedMappings = encodeURIComponent(encodedMappings);

			// TODO :: maybe don't call dry run but instead call the actual export and then check if there are missing mappings
			// and if we have all the mappings then we can proceed with the export
			if (this._fixedMapping.length === 0) {
				const missingMappings = await RestAPI.fetchAPI(
					`/rest/estimates/exportToXactimate?dryRun=true&estimateId=${estimateId}`,
					"POST",
					{
						fixedMappingBody: this._fixedMapping.length ? urlSafeEncodedMappings : "",
					},
				);
				if (missingMappings && missingMappings.length) {
					this.missingMappings = missingMappings;
					currentProjectStore.showLoadingAnimation = false;
					return;
				}
			}

			transactionId = await RestAPI.fetchAPI(`/rest/estimates/exportToXactimate?estimateId=${estimateId}`, "POST", {
				fixedMapping: this._fixedMapping.length ? urlSafeEncodedMappings : "",
			});
			if (transactionId) {
				this._fixedMapping = [];
				this.missingMappings = [];
				toastNotify("Estimate created! Uploading images... ", false);
				currentProjectStore.showLoadingAnimation = false;
				transactionId = await RestAPI.fetchAPI(
					`/rest/estimates/exportToXactimate?estimateId=${estimateId}&transactionId=${transactionId}&photosOnly=true`,
					"POST",
				);
				if (transactionId) {
					toastNotify("Images uploaded successfully", false);
				} else {
					currentProjectStore.setValue("uploadImagesFailed", true);
					toastNotify("Error uploading images", true);
				}
			} else {
				toastNotify("Error exporting to Xactimate", true);
			}
		} catch (e) {
			if (!transactionId) {
				toastNotify("Error exporting to Xactimate", true);
			} else {
				currentProjectStore.setValue("uploadImagesFailed", true);
				toastNotify("Error uploading images", true);
			}
		}

		currentProjectStore.showLoadingAnimation = false;
	}
}
const _veriskLocalStore = new VeriskLocalStore();
const ComboBox: React.FC<{ isDisabled?: boolean; startValue?: string; onChange: (val: string) => void }> = observer(
	({ isDisabled, startValue, onChange }) => {
		const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
			startValue
				? {
						value: startValue,
						label: startValue,
				  }
				: null,
		);

		const handleChange = (selected: SelectOption | null) => {
			setSelectedOption(selected);
			onChange(selected!.value);
		};

		return (
			<Select
				isDisabled={isDisabled}
				options={_veriskLocalStore.categories.map((m) => ({ value: m, label: m }))} // The predefined options
				value={selectedOption} // The current selected value
				onChange={handleChange} // Handler for changing the selected option
				isClearable={false} // Allow clearing the inputAGA
				// placeholder="Category"
				noOptionsMessage={(a) => (
					<div
						onClick={() => {
							_veriskLocalStore.addCategory(a.inputValue.toUpperCase());
						}}
						className="btn"
					>
						Click to Add {a.inputValue}
					</div>
				)} // Message when no options match
			/>
		);
	},
);
// TODO :: unite with EstimateEngineUtils.getInstance().RnRToActivity
function RnRToActivity(rnr: string): string {
	switch (rnr) {
		case "remove":
			return "-";
		case "replace":
			return "+";
		case "remove and replace":
			return "&";
		case "material only":
			return "m";
		case "install only":
			return "i";
		case "detach and reset":
			return "r";
	}
	return "";
}
const MissingMappingDialog: React.FC<{}> = observer(({}) => {
	const currentProjectStore = getEstimaticsCurrentProjectStore();
	// const [newMapping, setNewMapping] = useState<IXactimateToolRowData[]>([]);
	const [catValues, setCatValues] = useState<string[]>([]);
	const [selectorValues, setSelectorValues] = useState<string[]>([]);
	const [discardValues, setDiscardValues] = useState<boolean[]>([]);
	const [inputChanged, setInputChanged] = useState(0);
	const [canContinue, setCanContinue] = useState(false);

	useEffect(() => {
		if (inputChanged === 0) {
			const _catValues: string[] = Array(_veriskLocalStore.missingMappings.length).fill("");
			const _selectorValues: string[] = Array(_veriskLocalStore.missingMappings.length).fill("");
			for (let i = 0; i < _veriskLocalStore.missingMappings.length; i++) {
				const key = _veriskLocalStore.missingMappings[i].lineItem.replace(/\s/g, "").trim().toLowerCase();
				const item = window.localStorage.getItem(key);
				if (item) {
					const parsed = JSON.parse(item);
					logDebug("parsed", parsed.cat, parsed.variable);
					_catValues[i] = parsed.cat;
					_selectorValues[i] = parsed.variable;
				}
			}
			setCatValues(_catValues);
			setSelectorValues(_selectorValues);
			setDiscardValues(Array(_veriskLocalStore.missingMappings.length).fill(false));
			setInputChanged(1);
		}
	}, []);

	function getDefaultActivity(lineItem: string) {
		let activity = "+";
		const rnr = [" remove and replace ", " remove ", " replace ", " install only ", " material only ", " detach and reset "];
		const lineItemWithPadding = ` ${lineItem} `;
		const found = rnr.find((str) => lineItemWithPadding.includes(str));
		if (found) {
			activity = RnRToActivity(found.trim());
		}
		return activity;
	}

	function createNewMappingFromInput() {
		const ret = [] as IXactimateToolRowData[];
		for (let i = 0; i < _veriskLocalStore.missingMappings.length; i++) {
			const cat = catValues[i];
			const selector = selectorValues[i];
			const discard = discardValues[i];
			const measurement = (document.getElementById(`measurement-${i}`) as HTMLSelectElement).value;
			const activity = (document.getElementById(`activity-${i}`) as HTMLSelectElement).value;
			const lineItem = _veriskLocalStore.missingMappings[i].lineItem;
			// make sure we have all the values
			if (!discard) {
				if (cat.trim() === "" || selector.trim() === "" || measurement.trim() === "" || activity.trim() === "") {
					alert("Please fill all the fields");
					return null;
				}
			}
			ret.push({
				id: "",
				activity: activity.toUpperCase(),
				cat,
				acceptedLineString: lineItem,
				lineNoSpaces: lineItem.replace(/\s/g, "").trim().toLowerCase(),
				quantity: measurement,
				variable: selector,
				discard,
				selectedLineItemId: _veriskLocalStore.missingMappings[i].lineItemId,
			});
		}
		ret.forEach((r) => {
			logDebug("setItem", r.lineNoSpaces);
			window.localStorage.setItem(r.lineNoSpaces!, JSON.stringify(r));
		});
		return ret;
	}

	useEffect(() => {
		let isValid = true;
		for (let i = 0; i < _veriskLocalStore.missingMappings.length; i++) {
			if (discardValues[i]) {
				continue; // Skip validation for discarded items
			}
			// For non-discarded items, check if any required field is empty
			if (
				catValues[i]?.trim() === "" ||
				selectorValues[i]?.trim() === "" ||
				(document.getElementById(`measurement-${i}`) as HTMLSelectElement)?.value === ""
			) {
				isValid = false;
				break;
			}
		}
		setCanContinue(isValid);
	}, [catValues, selectorValues, inputChanged, discardValues]);

	return (
		<div className="w-[1026px] h-[600px] bg-white rounded-xl shadow flex-col justify-start items-start inline-flex relative ">
			<div className="w-full text-center p-4 text-xl">Please provide Xactimate codes for the following line items</div>
			<div className="h-[500px] w-[1024px] overflow-auto">
				<table className="table-fixed w-full border-collapse">
					<colgroup>
						<col className="w-8" />
						<col className="w-32" />
						<col className="w-32" />
						<col className="w-32" />
						<col className="w-32" />
						<col className="w-auto" />
					</colgroup>
					<thead>
						<tr>
							<th className="border">&nbsp;</th>
							<th className="border">Category</th>
							<th className="border">Selector</th>
							<th className="border">Activity</th>
							<th className="border">Measurement</th>
							<th className="border">Line Item</th>
						</tr>
					</thead>
					<tbody>
						{_veriskLocalStore.missingMappings.map((missingMapping, index) => {
							logDebug("missingMapping", missingMapping.calc, missingMapping.unit);
							return (
								<tr key={index} className={`border h-8`}>
									<td className="border">
										<div
											className="btn"
											onClick={() => {
												const newDiscardValues = [...discardValues];
												newDiscardValues[index] = !newDiscardValues[index];
												setDiscardValues(newDiscardValues);
											}}
										>
											{discardValues[index] ? (
												<img src="/assets/ui/undo.svg" className="w-5 h-5 ml-1" />
											) : (
												<img src="/assets/ui/trash-02.svg" className="w-5 h-5 ml-1" />
											)}
										</div>
									</td>
									<td className="border">
										{inputChanged > 0 && (
											<ComboBox
												isDisabled={discardValues[index]}
												startValue={catValues[index]}
												onChange={(value: string) => {
													const newCatValues = [...catValues];
													newCatValues[index] = value;
													setCatValues(newCatValues);
												}}
											/>
										)}
									</td>
									<td className="border">
										<input
											disabled={discardValues[index]}
											type="text"
											className={`w-full px-1 ${discardValues[index] ? "bg-gray-200" : ""}`}
											value={selectorValues[index]}
											onChange={(e) => {
												const newSelectorValues = [...selectorValues];
												newSelectorValues[index] = e.target.value;
												setSelectorValues(newSelectorValues);
											}}
										></input>
									</td>
									<td className="border">
										<select
											disabled={discardValues[index]}
											id={`activity-${index}`}
											className={`w-full border py-1 ${discardValues[index] ? "bg-gray-200" : ""}`}
											defaultValue={getDefaultActivity(missingMapping.lineItem)}
										>
											<option value={"-"}>- (rem)</option>
											<option value={"+"}>+ (r)</option>
											<option value={"&"}>& (r+r)</option>
											<option value={"m"}>m (mat)</option>
											<option value={"i"}>i (install)</option>
											<option value={"r"}>r (d+r)</option>
										</select>
									</td>
									<td className="border">
										<select
											disabled={discardValues[index]}
											onChange={() => {
												setInputChanged(inputChanged + 1);
											}}
											id={`measurement-${index}`}
											className={`border mx-2 max-w-28 ${discardValues[index] ? "bg-gray-200" : ""}`}
											defaultValue={missingMapping.unit || ""}
										>
											{["", ...EngineUtils.availableMeasurements].map((m, index) => {
												return (
													<option key={index} value={m}>
														{m}
													</option>
												);
											})}
										</select>
									</td>
									<td className="border ">
										<div
											className={`${
												discardValues[index] ? "line-through" : ""
											} px-1 self-stretch justify-start items-center flex`}
										>
											{missingMapping.lineItem}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<div className="h-32">&nbsp;</div>
			</div>
			<div className="w-full flex justify-between items-center px-5 py-3">
				<LassoSimpleButtonFull
					onClick={() => {
						_veriskLocalStore.missingMappings = [];
					}}
					caption="Cancel"
					customButtonStyle={{ height: 32, backgroundColor: "#bb0000" }}
				></LassoSimpleButtonFull>
				<LassoSimpleButtonFull
					onClick={() => {
						const newMapping = createNewMappingFromInput();
						if (!newMapping) return;

						logDebug("newMapping", newMapping);
						_veriskLocalStore.fixedMapping = newMapping;
						_veriskLocalStore.publishEstimate(currentProjectStore.estimaticsData.estimateId);
					}}
					caption="Continue"
					disabled={!canContinue}
					customButtonStyle={{
						height: 32,
					}}
				></LassoSimpleButtonFull>
			</div>
		</div>
	);
});

export default EstimateEnginePanel;
